// Performance mode: the CSS animation scrolls the track by one group stride.
// JS only measures that stride, sets the duration from the px/sec speed and adds
// copies of the group when one group is narrower than the slider.
function setupPerformanceMode($slider) {
    var autoplay   = $slider.getAttribute("data-autoplay") === "1";
    var speed      = parseFloat($slider.getAttribute("data-autoplay-speed")) || 60; // px/sec
    var isVertical = $slider.getAttribute("data-direction") === "vertical";
    var $wrapper   = $slider.querySelector(".mad-infinite-slider__wrapper");
    var $groups    = Array.from($wrapper.querySelectorAll(":scope > .mad-infinite-slider__group"));

    if (!autoplay || $groups.length < 2) return;

    // Copies are aria-hidden but stay clickable; keep them out of the tab order.
    $groups.slice(1).forEach(function ($group) {
        $group.querySelectorAll("a[href], button, input, select, textarea, [tabindex]").forEach(function (el) {
            el.setAttribute("tabindex", "-1");
        });
    });

    var lastGroups   = null;
    var lastDuration = null;

    function update() {
        // offsetLeft/Top ignore the running transform.
        var stride = isVertical
            ? $groups[1].offsetTop - $groups[0].offsetTop
            : $groups[1].offsetLeft - $groups[0].offsetLeft;
        if (stride <= 0) return;

        var container = isVertical ? $slider.clientHeight : $slider.clientWidth;
        var needed = Math.max(2, Math.ceil(container / stride) + 1);
        var added = false;
        while ($groups.length < needed) {
            var $copy = $groups[1].cloneNode(true);
            $wrapper.appendChild($copy);
            $groups.push($copy);
            added = true;
        }
        // Bricks skips lightbox links it has already initialised.
        if (added && typeof window.bricksPhotoswipe === "function") {
            window.bricksPhotoswipe();
        }

        var duration = (stride / speed).toFixed(3) + "s";
        if ($groups.length !== lastGroups) {
            $slider.style.setProperty("--mad-marquee-groups", $groups.length);
            lastGroups = $groups.length;
        }
        if (duration !== lastDuration) {
            $slider.style.setProperty("--mad-marquee-duration", duration);
            lastDuration = duration;
        }
        $slider.setAttribute("data-mad-marquee", "1");
    }

    // Fires once on observe. Appended groups resize neither the slider nor group 0,
    // and height-only changes (mobile address bar) leave both values unchanged.
    var observer = new ResizeObserver(update);
    observer.observe($slider);
    observer.observe($groups[0]);
}

// An open PhotoSwipe lightbox (Bricks PS5, WooCommerce PS4) pauses autoplay.
// Shared by all sliders and queried at most every 250ms instead of every frame.
var madLightboxCheckedAt = -Infinity;
var madLightboxOpen      = false;

function isMadLightboxOpen(now) {
    if (now - madLightboxCheckedAt > 250) {
        madLightboxCheckedAt = now;
        madLightboxOpen = !!document.querySelector(".pswp--open");
    }
    return madLightboxOpen;
}

function setupMadInfiniteSlider() {
    var hasGsap = typeof window.gsap !== "undefined";

    document.querySelectorAll(".mad-infinite-slider").forEach(function ($slider) {
        // Init-once guard — Bricks may re-invoke via $scripts on re-render.
        if ($slider.dataset.madInfiniteSliderReady) return;
        $slider.dataset.madInfiniteSliderReady = "1";

        $slider.removeAttribute("data-flickering");

        // CSS-only marquee: no GSAP engine, no listeners, no render loop.
        if ($slider.classList.contains("mad-infinite-slider--performance")) {
            setupPerformanceMode($slider);
            return;
        }

        // ---- Read config from data-* attributes ----
        var isVertical       = $slider.getAttribute("data-direction") === "vertical";
        var autoplay         = $slider.getAttribute("data-autoplay") === "1";
        var autoplaySpeed    = parseFloat($slider.getAttribute("data-autoplay-speed")) || 60; // px/sec
        var autoplayDirSign  = parseFloat($slider.getAttribute("data-autoplay-direction")) || 1; // 1 or -1
        var idleResumeMs     = parseInt($slider.getAttribute("data-idle-resume"), 10) || 3000;
        var pauseOnHover     = $slider.getAttribute("data-pause-on-hover") === "1";
        var motionDistortion = $slider.getAttribute("data-motion-distortion") === "1";
        var distIntensity    = parseFloat($slider.getAttribute("data-distortion-intensity")) || 1;

        var isBuilder = window.location.href.includes("brickspreview");

        var $wrapper = $slider.querySelector(".mad-infinite-slider__wrapper");
        var $items   = Array.from($slider.querySelectorAll(".mad-infinite-slider__item"));

        if ($items.length === 0) return;

        // ---- Interaction state (breakpoint-aware) ----
        // Drag comes from --mad-slider-interaction, the mouse wheel from
        // --mad-slider-wheel; Bricks outputs both per-breakpoint. Re-read on
        // resize so crossing a breakpoint enables/disables them live.
        var interactionEnabled = true;
        var wheelEnabled       = false;
        var wheelBound         = false;

        function readCssFlag(name) {
            return getComputedStyle($slider).getPropertyValue(name).trim();
        }

        function syncInteraction() {
            var drag  = readCssFlag("--mad-slider-interaction");
            var wheel = readCssFlag("--mad-slider-wheel");
            interactionEnabled = drag !== "disabled"; // empty/absent => enabled (default)
            // Instances saved before the wheel control existed have no value:
            // the wheel then follows the drag setting, as it did before.
            wheelEnabled = wheel ? wheel !== "disabled" : interactionEnabled;
            $slider.classList.toggle("mad-infinite-slider--no-interaction", !interactionEnabled);

            // Only bind the non-passive listener while the wheel is on, so the
            // page scrolls normally over the slider otherwise.
            if (wheelEnabled && !wheelBound) {
                $slider.addEventListener("wheel", onWheel, { passive: false });
                wheelBound = true;
            } else if (!wheelEnabled && wheelBound) {
                $slider.removeEventListener("wheel", onWheel, { passive: false });
                wheelBound = false;
            }
        }

        // ---- Axis helpers ----
        // axisKey:   GSAP property to set ("x" or "y")
        // sizeKey:   element measurement key ("clientWidth" or "clientHeight")
        var axisKey = isVertical ? "y" : "x";
        var sizeKey = isVertical ? "clientHeight" : "clientWidth";

        // ---- Gap resolver ----
        // Measures --gap in pixels by temporarily probing a hidden element.
        // Handles any CSS length: px, rem, vw, vh, %, var(), clamp(), calc() etc.
        function resolveGap() {
            var probe = document.createElement("div");
            probe.style.cssText = "position:absolute;visibility:hidden;pointer-events:none;height:0;width:var(--gap,0px)";
            $slider.appendChild(probe);
            var px = parseFloat(getComputedStyle(probe).width) || 0;
            $slider.removeChild(probe);
            return px;
        }

        // ---- Dimensions (recalculated on resize) ----
        // Each item is measured individually so items of differing sizes keep a
        // constant gap between them (no fixed per-item step).
        var itemSizes, itemOffsets, wrapTotal, gap;

        function updateDimensions() {
            gap = resolveGap();
            itemSizes = $items.map(function (el) { return el[sizeKey]; });
            itemOffsets = [];
            var acc = 0;
            for (var i = 0; i < $items.length; i++) {
                itemOffsets[i] = acc;
                acc += itemSizes[i] + gap;
            }
            // Loop circumference: sum of every item size plus one gap each.
            wrapTotal = acc;
        }
        updateDimensions();

        // ---- Infinite-loop positioning ----
        // Same result as gsap.utils.wrap(-span, wrapTotal - span, pos): each item
        // wraps on its own size so variable-height items stay flush.
        // GSAP quickSetters keep x/y in GSAP's transform cache, so the motion
        // distortion tweens (skew/rotate/scale) compose with it.
        var setters = hasGsap
            ? $items.map(function (el) { return gsap.quickSetter(el, axisKey, "px"); })
            : null;
        var lastDisposed = null;

        function dispose(scroll, force) {
            // Nothing moved: skip the style writes (idle slider, paused autoplay).
            if (!force && scroll === lastDisposed) return;
            lastDisposed = scroll;

            for (var i = 0; i < $items.length; i++) {
                var span = itemSizes[i] + gap;
                var pos = itemOffsets[i] + scroll;
                var wrapped = ((pos + span) % wrapTotal + wrapTotal) % wrapTotal - span;
                if (setters) {
                    setters[i](wrapped);
                } else {
                    $items[i].style.transform = isVertical
                        ? "translateY(" + wrapped + "px)"
                        : "translateX(" + wrapped + "px)";
                }
            }
        }

        // Initial render.
        dispose(0, true);

        // ---- State ----
        var scrollTarget = 0; // target position (eased toward)
        var scrollCurrent = 0; // current smoothed position
        var oldScrollCurrent = 0;

        var pausedByHover     = false;
        var pausedByUser      = false;
        var idleTimer         = null;
        var lastFrameTime     = null;

        // ---- Interaction handlers ----
        // Listeners are always attached; each gates on the live interactionEnabled
        // flag so responsiveness can toggle drag/scroll without re-init.
        function onUserInteract() {
            pausedByUser = true;
            if (idleTimer) clearTimeout(idleTimer);
            if (autoplay) {
                idleTimer = setTimeout(function () {
                    pausedByUser = false;
                }, idleResumeMs);
            }
        }

        // Wheel (bound/unbound by syncInteraction)
        function onWheel(e) {
            e.preventDefault();
            scrollTarget -= e.deltaY * 0.9;
            onUserInteract();
        }
        syncInteraction();

        // Drag / touch
        var touchStartPos = 0;
        var isDragging    = false;
        var dragDistance  = 0; // px moved since the last press, for click suppression

        function dragStart(e) {
            if (!interactionEnabled) return;
            touchStartPos = isVertical
                ? (e.clientY !== undefined ? e.clientY : e.touches[0].clientY)
                : (e.clientX !== undefined ? e.clientX : e.touches[0].clientX);
            isDragging   = true;
            dragDistance = 0;
            $slider.classList.add("mad-infinite-slider--is-dragging");
        }
        function dragMove(e) {
            if (!interactionEnabled) return;
            if (!isDragging) return;
            var current = isVertical
                ? (e.clientY !== undefined ? e.clientY : (e.touches && e.touches[0] ? e.touches[0].clientY : touchStartPos))
                : (e.clientX !== undefined ? e.clientX : (e.touches && e.touches[0] ? e.touches[0].clientX : touchStartPos));
            var delta = current - touchStartPos;
            dragDistance += Math.abs(delta);
            scrollTarget += delta * 2.5;
            touchStartPos = current;
            onUserInteract();
        }
        function dragEnd() {
            isDragging = false;
            $slider.classList.remove("mad-infinite-slider--is-dragging");
        }

        $slider.addEventListener("mousedown",   dragStart);
        $slider.addEventListener("mousemove",   dragMove);
        $slider.addEventListener("mouseleave",  dragEnd);
        $slider.addEventListener("mouseup",     dragEnd);
        $slider.addEventListener("touchstart",  dragStart, { passive: true });
        $slider.addEventListener("touchmove",   dragMove,  { passive: true });
        $slider.addEventListener("touchend",    dragEnd);
        $slider.addEventListener("selectstart", function () {
            if (!interactionEnabled) return;
            return false;
        });

        // Native link/image drag (e.g. a lightbox link) swallows mousemove/mouseup
        // and leaves the slider stuck in drag mode. CSS -webkit-user-drag only
        // covers Chromium/Safari; this covers Firefox too.
        $slider.addEventListener("dragstart", function (e) {
            if (interactionEnabled) e.preventDefault();
        });

        // A drag must not end in a click on a slide's link or lightbox image.
        // Capture phase on the slider runs before the handlers on its children
        // (e.g. Bricks' PhotoSwipe on the image link). Keyboard clicks
        // (detail 0) always pass.
        $slider.addEventListener("click", function (e) {
            if (e.detail === 0 || dragDistance <= 5) return;
            dragDistance = 0;
            e.preventDefault();
            e.stopPropagation();
        }, true);

        // Pause on hover (independent of drag).
        if (pauseOnHover) {
            $slider.addEventListener("mouseenter", function () { pausedByHover = true; });
            $slider.addEventListener("mouseleave", function () { pausedByHover = false; });
        }

        // ---- Motion distortion ----
        // Reproduces the old per-frame gsap.to({…, overwrite: "auto"}) (0.5s,
        // power1.out) without creating tweens. Each frame the previous frame's
        // tween rendered at 2 frames of progress, then the new tween started from
        // that value, rendered 1 frame toward the new target and killed the old one.
        // So: a base value following the previous target at 2-frame steps, plus a
        // 1-frame lead toward the current target. Writes stop once settled.
        var distort = null;
        if (motionDistortion && hasGsap) {
            distort = {
                setters: [
                    gsap.quickSetter($items, isVertical ? "skewY" : "skewX", "deg"),
                    gsap.quickSetter($items, "rotation", "deg"),
                    gsap.quickSetter($items, "scaleX"),
                    gsap.quickSetter($items, "scaleY"),
                ],
                base:    [0, 0, 1, 1], // skew, rotation, scaleX, scaleY
                target:  [0, 0, 1, 1],
                settled: true,
            };
        }

        function distortEase(t) {
            var p = Math.min(t / 0.5, 1);
            return 1 - (1 - p) * (1 - p); // power1.out over 0.5s
        }

        function updateDistortion(velocity, dt) {
            var next = distort.target;
            if (Math.abs(velocity) > 0.01) {
                var v = velocity * distIntensity;
                var scale = 1 - Math.min(100, Math.abs(v)) * 0.003;
                next = [isVertical ? v * 0.2 : v * -0.2, isVertical ? v * -0.01 : v * 0.01, scale, scale];
                distort.settled = false;
            }
            if (distort.settled) return;

            var e1 = distortEase(dt);
            var e2 = distortEase(2 * dt);
            var done = true;
            for (var i = 0; i < 4; i++) {
                var base = distort.base[i] + (distort.target[i] - distort.base[i]) * e2;
                var value = base + (next[i] - base) * e1;
                if (Math.abs(next[i] - value) > 0.0001) done = false;
                distort.base[i] = base;
                distort.setters[i](value);
            }
            distort.target = next;
            if (done) {
                for (var j = 0; j < 4; j++) distort.setters[j](next[j]);
                distort.base = next.slice();
                distort.settled = true;
            }
        }

        // ---- RAF render loop ----
        function lerp(v0, v1, t) { return v0 * (1 - t) + v1 * t; }

        var rafId = null;

        function renderLoop(timestamp) {
            rafId = requestAnimationFrame(renderLoop);

            // Delta time for frame-rate-independent autoplay.
            var dt = 0;
            if (lastFrameTime !== null) {
                dt = Math.min((timestamp - lastFrameTime) / 1000, 0.1); // cap at 100ms
            }
            lastFrameTime = timestamp;

            // Advance autoplay target.
            var isAutoplayActive = autoplay && !pausedByHover && !pausedByUser && !isBuilder
                && !isMadLightboxOpen(timestamp);
            if (isAutoplayActive) {
                scrollTarget += autoplaySpeed * dt * autoplayDirSign * -1;
                // Sign: negative = move items leftward/upward (natural forward motion).
            }

            // Smooth current toward target; snap the sub-pixel tail so an idle
            // slider stops writing styles (dispose skips unchanged positions).
            scrollCurrent = lerp(scrollCurrent, scrollTarget, 0.1);
            if (Math.abs(scrollTarget - scrollCurrent) < 0.05) scrollCurrent = scrollTarget;
            dispose(scrollCurrent);

            // Velocity-based motion distortion.
            var velocity = scrollCurrent - oldScrollCurrent;
            oldScrollCurrent = scrollCurrent;

            if (distort) updateDistortion(velocity, dt);
        }

        function startLoop() {
            if (rafId !== null) return;
            lastFrameTime = null; // no dt jump after a pause
            rafId = requestAnimationFrame(renderLoop);
        }

        function stopLoop() {
            if (rafId === null) return;
            cancelAnimationFrame(rafId);
            rafId = null;
        }

        // Run the loop only while the slider is (nearly) on screen.
        if (typeof IntersectionObserver === "function") {
            new IntersectionObserver(function (entries) {
                if (entries[entries.length - 1].isIntersecting) startLoop();
                else stopLoop();
            }, { rootMargin: "100px" }).observe($slider);
        } else {
            startLoop();
        }

        // ---- Resize ----
        window.addEventListener("resize", function () {
            syncInteraction();
            updateDimensions();
            dispose(scrollCurrent, true);
        });
    });
}

document.addEventListener("DOMContentLoaded", function () {
    var isBuilder = window.location.href.includes("brickspreview");

    if (isBuilder) {
        // In the builder, re-initialise when Bricks adds new slider nodes.
        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.addedNodes.length) {
                    mutation.addedNodes.forEach(function (node) {
                        if (node.classList && node.classList.contains("mad-infinite-slider")) {
                            setTimeout(setupMadInfiniteSlider, 100);
                        }
                        // Also check descendants (Bricks wraps in a container div).
                        if (node.querySelectorAll) {
                            var found = node.querySelectorAll(".mad-infinite-slider");
                            if (found.length) setTimeout(setupMadInfiniteSlider, 100);
                        }
                    });
                }
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
    } else {
        setupMadInfiniteSlider();
    }
});

window.setupMadInfiniteSlider = setupMadInfiniteSlider;
